import { useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/appConfig';

/**
 * useMIQHealth — Manages AI MIQ scoring, metabolic profile, body type guidance,
 * plateau diagnostics, MIQ UI accordion, and nudge system.
 * Extracted from App.js Phase 5 Heavy Batch Round 2.
 */
export default function useMIQHealth({ userId }) {
  // ── MIQ Scoring State ──────────────────────────────────────────────────────
  const [aiMiq, setAiMiq] = useState(null);
  const [aiMiqLoading, setAiMiqLoading] = useState(false);

  // ── MIQ Calibration ────────────────────────────────────────────────────────
  const [showCalibrationCompleteModal, setShowCalibrationCompleteModal] = useState(false);
  const [calibrationCompleteDismissed, setCalibrationCompleteDismissed] = useState(false);

  // ── Metabolic Profile ──────────────────────────────────────────────────────
  const [metabolicProfile, setMetabolicProfile] = useState(null);
  const [loadingMetabolicProfile, setLoadingMetabolicProfile] = useState(false);

  // ── Plateau Diagnostics ────────────────────────────────────────────────────
  const [plateauDiagnostics, setPlateauDiagnostics] = useState(null);
  const [showPlateauCard, setShowPlateauCard] = useState(false);

  // ── MIQ Nudge System ───────────────────────────────────────────────────────
  const [miqNudge, setMiqNudge] = useState(null);
  const [miqNudgeShownThisSession, setMiqNudgeShownThisSession] = useState(false);

  // ── Body Type Guidance ─────────────────────────────────────────────────────
  const [bodyTypeGuidance, setBodyTypeGuidance] = useState(null);
  const [showBodyTypeGuidance, setShowBodyTypeGuidance] = useState(false);
  const [allBodyTypeGuidance, setAllBodyTypeGuidance] = useState(null);

  // ── MIQ Accordion Expansion ────────────────────────────────────────────────
  const [expandWhatIsMiq, setExpandWhatIsMiq] = useState(false);
  const [expandMiqFactors, setExpandMiqFactors] = useState(false);
  const [expandMiqConfidence, setExpandMiqConfidence] = useState(false);

  // ── Load Plateau Diagnostics ───────────────────────────────────────────────
  const loadPlateauDiagnostics = useCallback(async (uid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/plateau-diagnostics/${uid}`);
      if (response.ok) {
        const data = await response.json();
        setPlateauDiagnostics(data);
        setShowPlateauCard(data.plateauDetected === true);
      }
    } catch (error) {
      console.error('Error loading plateau diagnostics:', error);
    }
  }, []);

  // ── Load Metabolic Profile ─────────────────────────────────────────────────
  const loadMetabolicProfile = useCallback(async (uid) => {
    try {
      setLoadingMetabolicProfile(true);
      const response = await fetch(`${BACKEND_URL}/api/metabolic-profile/${uid}`);
      if (response.ok) {
        const data = await response.json();
        setMetabolicProfile(data);
        await loadPlateauDiagnostics(uid);
      }
    } catch (error) {
      console.error('Error loading metabolic profile:', error);
    } finally {
      setLoadingMetabolicProfile(false);
    }
  }, [loadPlateauDiagnostics]);

  // ── Generate Metabolic Profile ─────────────────────────────────────────────
  const generateMetabolicProfile = useCallback(async () => {
    try {
      setLoadingMetabolicProfile(true);
      const response = await fetch(`${BACKEND_URL}/api/metabolic-profile/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        await loadMetabolicProfile(userId);
        alert('✅ Metabolic Profile generated successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error generating metabolic profile:', error);
      alert('Failed to generate metabolic profile');
    } finally {
      setLoadingMetabolicProfile(false);
    }
  }, [userId, loadMetabolicProfile]);

  // ── Load AI MIQ ────────────────────────────────────────────────────────────
  const loadAiMiq = useCallback(async (uid) => {
    try {
      setAiMiqLoading(true);
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      const [miqRes, scoreRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ai-miq/${uid}?tz_offset=${tzOffset}`),
        fetch(`${BACKEND_URL}/api/miq-score/history/${uid}?limit=12`).catch(() => null)
      ]);
      if (miqRes.ok) {
        const data = await miqRes.json();
        if (scoreRes && scoreRes.ok) {
          const scoreData = await scoreRes.json();
          data.score_history = scoreData.scores || [];
          data.dynamic_score = scoreData.current_score;
          if (scoreData.scores?.length > 0) {
            const latest = scoreData.scores[scoreData.scores.length - 1];
            data.latest_brief = {
              score_delta: latest.delta,
              score_after: latest.score,
              week_number: latest.week_number,
              factors: latest.factors
            };
          }
        }
        setAiMiq(data);
      }
    } catch (error) {
      console.error('Error loading MIQ:', error);
    } finally {
      setAiMiqLoading(false);
    }
  }, []);

  // ── Load Body Type Guidance ────────────────────────────────────────────────
  const loadBodyTypeGuidance = useCallback(async (bodyType) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/body-type/nutrition-guidance/${encodeURIComponent(bodyType)}`);
      if (response.ok) {
        const data = await response.json();
        setBodyTypeGuidance(data);
      }
    } catch (error) {
      console.error('Error loading body type guidance:', error);
    }
  }, []);

  // ── Load All Body Type Guidance ────────────────────────────────────────────
  const loadAllBodyTypeGuidance = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/body-type/all-guidance`);
      if (response.ok) {
        const data = await response.json();
        setAllBodyTypeGuidance(data.guidance);
      }
    } catch (error) {
      console.error('Error loading all body type guidance:', error);
    }
  }, []);

  // ── Generic insights for users without profile ─────────────────────────────
  const getGenericInsight = useCallback((meal) => {
    const protein = meal?.protein || 0;
    const carbs = meal?.carbs || 0;
    const fat = meal?.fat || 0;
    const fiber = meal?.fiber || 0;
    const calories = meal?.calories || 0;

    const insights = [];

    if (protein < 15) {
      insights.push({ icon: '💪', text: 'This meal looks protein-light for most people. Adding eggs, chicken, or beans could help.' });
    }
    if (fiber < 3 && carbs > 30) {
      insights.push({ icon: '🥗', text: 'Nice carbs! Adding fiber-rich veggies could improve fullness and digestion.' });
    }
    if (fat > 25 && protein < 20) {
      insights.push({ icon: '⚖️', text: 'Higher fat meal — pairing with protein can help you stay satisfied longer.' });
    }
    if (calories > 600 && protein >= 20 && fiber >= 3) {
      insights.push({ icon: '✨', text: 'Nice balance! This meal has a good mix of nutrients.' });
    }
    if (calories < 300) {
      insights.push({ icon: '🍽️', text: 'Light meal — great for a snack, but you might get hungry soon.' });
    }

    if (insights.length > 0) {
      return insights[Math.floor(Math.random() * insights.length)];
    }
    return { icon: '📊', text: 'Meal logged! Complete your profile for personalized insights.' };
  }, []);

  return {
    aiMiq, setAiMiq,
    aiMiqLoading,
    showCalibrationCompleteModal, setShowCalibrationCompleteModal,
    calibrationCompleteDismissed, setCalibrationCompleteDismissed,
    metabolicProfile, setMetabolicProfile,
    loadingMetabolicProfile, setLoadingMetabolicProfile,
    plateauDiagnostics,
    showPlateauCard,
    miqNudge, setMiqNudge,
    miqNudgeShownThisSession, setMiqNudgeShownThisSession,
    bodyTypeGuidance, setBodyTypeGuidance,
    showBodyTypeGuidance, setShowBodyTypeGuidance,
    allBodyTypeGuidance,
    expandWhatIsMiq, setExpandWhatIsMiq,
    expandMiqFactors, setExpandMiqFactors,
    expandMiqConfidence, setExpandMiqConfidence,
    loadAiMiq,
    loadMetabolicProfile,
    generateMetabolicProfile,
    loadPlateauDiagnostics,
    loadBodyTypeGuidance,
    loadAllBodyTypeGuidance,
    getGenericInsight
  };
}
